import { Grid, GridItem, Box, Text, Heading, Icon, useColorModeValue } from '@chakra-ui/react';
import { Users, CheckCircle, AlertCircle, XCircle, TrendingUp } from 'lucide-react';

interface RocksStatsCardsProps {
  stats: {
    total: number;
    onTrack: number;
    offTrack: number;
    avgProgress: number;
  };
  currentFilter?: 'all' | 'on-track' | 'off-track';
  onFilterChange?: (filter: 'all' | 'on-track' | 'off-track') => void;
}

/**
 * Rocks Stats Cards Component
 * Displays dashboard statistics in a grid of 5 cards
 * Migrated from Tailwind to Chakra UI
 */
export function RocksStatsCards({ stats, currentFilter = 'all', onFilterChange }: RocksStatsCardsProps) {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderBlue = useColorModeValue('blue.200', 'blue.600');
  const borderGreen = useColorModeValue('green.200', 'green.600');
  const borderRed = useColorModeValue('red.200', 'red.600');
  const borderPurple = useColorModeValue('purple.200', 'purple.600');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');

  const bgActive = useColorModeValue('gray.50', 'gray.600');

  const getBorderWidth = (isActive: boolean) => isActive ? '4px' : '2px';

  return (
    <Grid templateColumns="repeat(4, 1fr)" gap={4}>
      {/* Total Rocks */}
      <GridItem>
        <Box
          bg={currentFilter === 'all' ? bgActive : cardBg}
          rounded="lg"
          border={getBorderWidth(currentFilter === 'all')}
          borderColor={borderBlue}
          p={4}
          cursor={onFilterChange ? 'pointer' : 'default'}
          onClick={() => onFilterChange?.('all')}
          transition="all 0.2s"
          _hover={{ transform: onFilterChange ? 'translateY(-2px)' : 'none', shadow: 'md' }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Text fontSize="sm" color={labelColor} fontWeight={currentFilter === 'all' ? 'bold' : 'normal'}>
              Total Rocks
            </Text>
            <Icon as={Users} w={8} h={8} color="blue.500" />
          </Box>
          <Heading size="2xl" color={headingColor}>
            {stats.total}
          </Heading>
        </Box>
      </GridItem>

      {/* On Track */}
      <GridItem>
        <Box
          bg={currentFilter === 'on-track' ? bgActive : cardBg}
          rounded="lg"
          border={getBorderWidth(currentFilter === 'on-track')}
          borderColor={borderGreen}
          p={4}
          cursor={onFilterChange ? 'pointer' : 'default'}
          onClick={() => onFilterChange?.('on-track')}
          transition="all 0.2s"
          _hover={{ transform: onFilterChange ? 'translateY(-2px)' : 'none', shadow: 'md' }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Text fontSize="sm" color={labelColor} fontWeight={currentFilter === 'on-track' ? 'bold' : 'normal'}>
              On Track
            </Text>
            <Icon as={CheckCircle} w={8} h={8} color="green.500" />
          </Box>
          <Heading size="2xl" color="green.600">
            {stats.onTrack}
          </Heading>
        </Box>
      </GridItem>

      {/* Off Track */}
      <GridItem>
        <Box
          bg={currentFilter === 'off-track' ? bgActive : cardBg}
          rounded="lg"
          border={getBorderWidth(currentFilter === 'off-track')}
          borderColor={borderRed}
          p={4}
          cursor={onFilterChange ? 'pointer' : 'default'}
          onClick={() => onFilterChange?.('off-track')}
          transition="all 0.2s"
          _hover={{ transform: onFilterChange ? 'translateY(-2px)' : 'none', shadow: 'md' }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Text fontSize="sm" color={labelColor} fontWeight={currentFilter === 'off-track' ? 'bold' : 'normal'}>
              Off Track
            </Text>
            <Icon as={XCircle} w={8} h={8} color="red.500" />
          </Box>
          <Heading size="2xl" color="red.600">
            {stats.offTrack}
          </Heading>
        </Box>
      </GridItem>

      {/* Avg Progress */}
      <GridItem>
        <Box
          bg={cardBg}
          rounded="lg"
          border="2px"
          borderColor={borderPurple}
          p={4}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Text fontSize="sm" color={labelColor}>
              Avg Progress
            </Text>
            <Icon as={TrendingUp} w={8} h={8} color="purple.500" />
          </Box>
          <Heading size="2xl" color="purple.600">
            {stats.avgProgress}%
          </Heading>
        </Box>
      </GridItem>
    </Grid>
  );
}
